import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { FaEdit, FaTrashAlt, FaSearch, FaPlus, FaQuestionCircle, FaChartBar, FaFilter, FaDownload, FaEye, FaSort, FaSortUp, FaSortDown, FaIndustry, FaCode, FaTag, FaCalendarAlt, FaTimes } from 'react-icons/fa';
import { grid } from "ldrs";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import i18n from '../../../i18n';

// Register the grid loader
grid.register();

function normalizeValueUnits(raw) {
  if (Array.isArray(raw)) {
    return raw.filter((u) => typeof u === 'string' && u.trim()).map((u) => u.trim());
  }
  if (raw == null || raw === '') return [];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter((u) => typeof u === 'string').map((u) => u.trim()).filter(Boolean)
        : [];
    } catch {
      return raw.split(',').map((s) => s.trim()).filter(Boolean);
    }
  }
  return [];
}

/** YYYY-MM-DD for <input type="date" /> from API string or Date */
function toDateInputValue(val) {
  if (val == null || val === '') return '';
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.slice(0, 10);
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDisplayDate(val) {
  if (val == null || val === '') return '—';
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.slice(0, 10);
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return String(val);
  return d.toLocaleDateString();
}

const ListQuestionsForManagement = () => {
  const { t } = useTranslation();
  
  // Helper function to get translated question text
  const getQuestionText = (question) => {
    const currentLanguage = i18n.language || localStorage.getItem('i18nextLng') || 'en';
    const isSpanish = currentLanguage === 'es' || currentLanguage.startsWith('es');
    
    if (isSpanish && question.engagementQuestionEs) {
      return question.engagementQuestionEs;
    }
    return question.engagementQuestion || '';
  };
  const location = useLocation();
  // Read URL parameters
  const searchParams = new URLSearchParams(location.search);
  const openModalParam = searchParams.get('openModal');
  
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [viewQuestion, setViewQuestion] = useState(null);
  const [showModal, setShowModal] = useState(openModalParam === 'true');
  const [disclosureTopics, setDisclosureTopics] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState('topicCode');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterSector, setFilterSector] = useState('all');
  const [filterIndustry, setFilterIndustry] = useState('all');
  /** 'all' | 'yes' | 'no' — Section 9.1 CMF supplemental questions */
  const [filterSection91, setFilterSection91] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [showSubQuestionsModal, setShowSubQuestionsModal] = useState(false);
  const [selectedQuestionForSubQuestions, setSelectedQuestionForSubQuestions] = useState(null);
  const [subQuestions, setSubQuestions] = useState([]);
  const [newSubQuestion, setNewSubQuestion] = useState({ subQuestionText: '' });
  const [stats, setStats] = useState({
    totalQuestions: 0,
    totalSectors: 0,
    totalIndustries: 0,
    avgQuestionsPerSector: 0
  });
  const [newUnitTagInput, setNewUnitTagInput] = useState('');
  const [editUnitTagInput, setEditUnitTagInput] = useState('');

  const [newQuestion, setNewQuestion] = useState({
    sectorCode: '',
    topicCode: '',
    industry: '',
    industryCode: '',
    sector: '',
    numberOfQuestions: '',
    questionIdentifier: '',
    engagementQuestion: '',
    engagementQuestionEs: '',
    cmfRequirement: '',
    cmfRequirementEs: '',
    disclosureTopic: '',
    disclosureTopicFieldId: '',
    section9_1: false,
    ncg30461Article: '',
    ncg30519Article: '',
    valueUnitFormat: [],
    sasbMetricName: '',
    reportingPeriodStart: '',
    reportingPeriodEnd: '',
    answerInputKind: 'text',
    fillInBlanksTemplate: '',
    answerIncludeUnitDropdown: false,
    showAnswerStatusOptions: true
  });

  // Skeleton Components
  const SkeletonStatsCard = () => (
    <div className="bg-white rounded-lg shadow border border-gray-100 p-3 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="h-3 bg-gray-200 rounded w-20 mb-2"></div>
          <div className="h-6 bg-gray-200 rounded w-12 mb-1"></div>
          <div className="h-2 bg-gray-200 rounded w-24"></div>
        </div>
        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full"></div>
      </div>
    </div>
  );

  const SkeletonMobileCard = () => (
    <div className="bg-gray-50 rounded p-2 border border-gray-200 animate-pulse">
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center space-x-1">
          <div className="w-5 h-5 bg-gray-200 rounded-full"></div>
          <div className="h-4 bg-gray-200 rounded w-8"></div>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-6 h-6 bg-gray-200 rounded"></div>
          <div className="w-6 h-6 bg-gray-200 rounded"></div>
          <div className="w-6 h-6 bg-gray-200 rounded"></div>
        </div>
      </div>
      <div className="space-y-1 mb-1">
        <div className="h-3 bg-gray-200 rounded w-full"></div>
        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
      </div>
      <div className="flex items-center space-x-3">
        <div className="h-3 bg-gray-200 rounded w-20"></div>
        <div className="h-3 bg-gray-200 rounded w-24"></div>
      </div>
      <div className="mt-1">
        <div className="h-3 bg-gray-200 rounded w-32"></div>
      </div>
    </div>
  );

  const SkeletonTableRow = () => (
    <tr className="animate-pulse">
      <td className="px-2 py-2 whitespace-nowrap text-center">
        <div className="w-5 h-5 bg-gray-200 rounded-full mx-auto"></div>
      </td>
      <td className="px-2 py-2 whitespace-nowrap text-center">
        <div className="h-4 w-4 bg-gray-200 rounded mx-auto"></div>
      </td>
      <td className="px-2 py-2 whitespace-nowrap">
        <div className="h-4 bg-gray-200 rounded w-24"></div>
      </td>
      <td className="px-2 py-2 whitespace-nowrap">
        <div className="h-4 bg-gray-200 rounded w-24"></div>
      </td>
      <td className="px-2 py-2 whitespace-nowrap">
        <div className="h-4 bg-gray-200 rounded w-20"></div>
      </td>
      <td className="px-2 py-2">
        <div className="space-y-1">
          <div className="h-3 bg-gray-200 rounded w-full"></div>
          <div className="h-3 bg-gray-200 rounded w-2/3"></div>
        </div>
      </td>
      {/* Number of questions column hidden for now — keep skeleton colspan via actions only */}
      <td className="px-2 py-2 whitespace-nowrap text-center">
        <div className="flex items-center justify-center space-x-1">
          <div className="w-6 h-6 bg-gray-200 rounded"></div>
          <div className="w-6 h-6 bg-gray-200 rounded"></div>
          <div className="w-6 h-6 bg-gray-200 rounded"></div>
        </div>
      </td>
    </tr>
  );
  // Filter and search functionality
  useEffect(() => {
    const currentLanguage = i18n.language || localStorage.getItem('i18nextLng') || 'en';
    const isSpanish = currentLanguage === 'es' || currentLanguage.startsWith('es');
    
    let filtered = questions.filter((question) => {
      const questionText = getQuestionText(question);
      const searchLower = searchTerm.toLowerCase();
      
      return (
        questionText.toLowerCase().includes(searchLower) ||
        question.engagementQuestion?.toLowerCase().includes(searchLower) ||
        (isSpanish && question.engagementQuestionEs?.toLowerCase().includes(searchLower)) ||
        question.sector.toLowerCase().includes(searchLower) ||
        question.industry.toLowerCase().includes(searchLower) ||
        (question.topicCode || '').toLowerCase().includes(searchLower)
      );
    });

    // Apply sector filter
    if (filterSector !== 'all') {
      filtered = filtered.filter(question => question.sector === filterSector);
    }

    // Apply industry filter
    if (filterIndustry !== 'all') {
      filtered = filtered.filter(question => question.industry === filterIndustry);
    }

    if (filterSection91 === 'yes') {
      filtered = filtered.filter((question) => Boolean(question.section9_1));
    } else if (filterSection91 === 'no') {
      filtered = filtered.filter((question) => !Boolean(question.section9_1));
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle numeric sorting for ID
      if (sortField === 'id') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      } else if (sortField === 'engagementQuestion') {
        // Use translated question for sorting
        aValue = (getQuestionText(a) || '').toLowerCase();
        bValue = (getQuestionText(b) || '').toLowerCase();
      } else if (sortField === 'sector' || sortField === 'industry' || sortField === 'topicCode') {
        aValue = (aValue || '').toLowerCase();
        bValue = (bValue || '').toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredQuestions(filtered);
  }, [searchTerm, questions, filterSector, filterIndustry, filterSection91, sortField, sortDirection]);

  const handleDelete = async (questionId) => {
    if (!window.confirm(t('questionsManagement.confirmDeleteQuestion'))) return;

    try {
      const response = await axios.delete('/questions-management', {
        data: { questionId }
      });

      if (response.data.status) {
        fetchQuestions(); // refresh list
        toastr.success(t('questionsManagement.questionDeletedSuccessfully'));
      } else {
        toastr.error(response.data.message || t('questionsManagement.failedToDeleteQuestion'));
      }
    } catch (err) {
      toastr.error(err.response?.data?.error || t('questionsManagement.deleteFailed'));
    }
  };

  const fetchQuestions = async () => {
    try {
      const response = await axios.get('/questions-management');
      if (response.data.status) {
        const raw = response.data.questions || [];
        const questionsData = raw.map((q) => ({
          ...q,
          ncg30519Article: q.ncg30519Article ?? q.ncg30461Article2 ?? '',
        }));
        setQuestions(questionsData);
        setFilteredQuestions(questionsData);

        // Calculate stats
        const uniqueSectors = [...new Set(questionsData.map(q => q.sector))];
        const uniqueIndustries = [...new Set(questionsData.map(q => q.industry))];

        setStats({
          totalQuestions: questionsData.length,
          totalSectors: uniqueSectors.length,
          totalIndustries: uniqueIndustries.length,
          avgQuestionsPerSector: questionsData.length > 0 ? Math.round(questionsData.length / uniqueSectors.length) : 0
        });
      } else {
        setError(t('questionsManagement.failedToFetchQuestions'));
      }
    } catch (err) {
      setError(err.response?.data?.error || t('questionsManagement.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchDisclosureTopics = async () => {
    try {
      const response = await axios.get('/questions-management/disclosure-topics');
      if (response.data.status) {
        setDisclosureTopics(response.data.topics);
      } else {
        setError(t('questionsManagement.failedToFetchDisclosureTopics'));
      }
    } catch (err) {
      setError(err.response?.data?.error || t('questionsManagement.serverError'));
    }
  };

  const fetchSectors = async () => {
    try {
      const response = await axios.get('/step2-values');
      if (response.data.status) {
        setSectors(response.data.step2Values || []);
      }
    } catch (err) {
      console.error('Error fetching sectors:', err);
    }
  };

  const fetchIndustries = async () => {
    try {
      const response = await axios.get('/step3-values?all=true');
      if (response.data.status) {
        setIndustries(response.data.step3Values || []);
      }
    } catch (err) {
      console.error('Error fetching industries:', err);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const payload = {
      ...selectedQuestion,
      questionIdentifier: String(selectedQuestion.topicCode ?? '').trim(),
    };
    try {
      const response = await axios.put(`/questions-management/${selectedQuestion.id}`, payload);
      if (response.data.status) {
        fetchQuestions();
        setShowModal(false);
        setSelectedQuestion(null);
        toastr.success(t('questionsManagement.questionUpdatedSuccessfully'));
      } else {
        toastr.error(response.data.message);
      }
    } catch (err) {
      toastr.error(err.response?.data?.error || t('questionsManagement.updateFailed'));
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    const payload = {
      ...newQuestion,
      questionIdentifier: String(newQuestion.topicCode ?? '').trim(),
    };
    try {
      const response = await axios.post('/questions-management', payload);
      if (response.data.status) {
        fetchQuestions();
        setNewQuestion({
          sectorCode: '',
          topicCode: '',
          industry: '',
          industryCode: '',
          sector: '',
          numberOfQuestions: 1,
          questionIdentifier: '',
          engagementQuestion: '',
          engagementQuestionEs: '',
          cmfRequirement: '',
          cmfRequirementEs: '',
          disclosureTopic: '',
          disclosureTopicFieldId: '',
          section9_1: false,
          ncg30461Article: '',
          ncg30519Article: '',
          valueUnitFormat: [],
          sasbMetricName: '',
          reportingPeriodStart: '',
          reportingPeriodEnd: '',
          answerInputKind: 'text',
          fillInBlanksTemplate: '',
          answerIncludeUnitDropdown: false,
          showAnswerStatusOptions: true
        });
        setNewUnitTagInput('');
        setShowModal(false);
        toastr.success(t('questionsManagement.questionAddedSuccessfully'));
      } else {
        toastr.error(response.data.message);
      }
    } catch (err) {
      toastr.error(err.response?.data?.error || t('questionsManagement.addQuestionFailed'));
    }
  };

  // Subquestion management functions
  const handleOpenSubQuestions = async (question) => {
    setSelectedQuestionForSubQuestions(question);
    try {
      const response = await axios.get(`/subquestions/questions/${question.id}/subquestions`);
      if (response.data.status) {
        setSubQuestions(response.data.subQuestions);
      }
    } catch (error) {
      console.error('Error fetching subquestions:', error);
      toastr.error(t('questionsManagement.failedToFetchSubquestions'));
    }
    setShowSubQuestionsModal(true);
  };

  const handleAddSubQuestion = async () => {
    if (!newSubQuestion.subQuestionText.trim()) {
      toastr.error(t('questionsManagement.subquestionTextRequired'));
      return;
    }

    try {
      const response = await axios.post('/subquestions/subquestions', {
        questionId: selectedQuestionForSubQuestions.id,
        subQuestionText: newSubQuestion.subQuestionText
      });

      if (response.data.status) {
        setSubQuestions(prev => [...prev, response.data.subQuestion]);
        setNewSubQuestion({ subQuestionText: '' });
        toastr.success(t('questionsManagement.subquestionAddedSuccessfully'));
      } else {
        toastr.error(response.data.message);
      }
    } catch (error) {
      console.error('Error adding subquestion:', error);
      toastr.error(t('questionsManagement.failedToAddSubquestion'));
    }
  };

  const handleDeleteSubQuestion = async (subQuestionId) => {
    if (!window.confirm(t('questionsManagement.confirmDeleteSubquestion'))) return;

    try {
      const response = await axios.delete(`/subquestions/subquestions/${subQuestionId}`);
      if (response.data.status) {
        setSubQuestions(prev => prev.filter(sq => sq.id !== subQuestionId));
        toastr.success(t('questionsManagement.subquestionDeletedSuccessfully'));
      } else {
        toastr.error(response.data.message);
      }
    } catch (error) {
      console.error('Error deleting subquestion:', error);
      toastr.error(t('questionsManagement.failedToDeleteSubquestion'));
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectQuestion = (questionId) => {
    setSelectedQuestions(prev =>
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleSelectAll = () => {
    if (selectedQuestions.length === filteredQuestions.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(filteredQuestions.map(question => question.id));
    }
  };

  const exportQuestions = () => {
    const csvContent = [
      ['ID', 'Sector Code', 'Topic Code', 'Industry', 'Industry Code', 'Sector', 'Engagement Question', 'Created At'],
      ...filteredQuestions.map(question => [
        question.id,
        question.sectorCode,
        question.topicCode,
        question.industry,
        question.industryCode,
        question.sector,
        getQuestionText(question),
        new Date(question.createdAt).toLocaleDateString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'questions.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchQuestions();
    fetchDisclosureTopics();
    fetchSectors();
    fetchIndustries();
  }, []);

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-gray-50 font-sans antialiased">
      {/* Compact Header Section - Responsive */}
      <div className="bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 shadow-lg relative overflow-hidden">
        <div className="max-w-7xl 2xl:max-w-[85%] mx-auto 2xl:mx-0 2xl:ml-[5%] px-3 sm:px-4 py-3 sm:py-4 relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow">
                <FaQuestionCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-white mb-1">{t('questionsManagement.title')}</h1>
                <p className="text-green-100 text-xs sm:text-sm">{t('questionsManagement.subtitle')}</p>
              </div>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-xs text-green-100">{t('reporting.lastUpdated')}: {new Date().toLocaleDateString()}</p>
              <p className="text-sm text-lime-300 font-bold">{stats.totalQuestions} {t('questionsManagement.totalQuestions')}</p>
              <p className="text-xs text-green-200">{t('questionsManagement.showing')}: {filteredQuestions.length} {t('questionsManagement.filtered')}</p>
            </div>
          </div>
        </div>
      </div>
  
      {/* Compact Stats Cards - Responsive */}
      <div className="w-full max-w-full mx-auto 2xl:mx-0 2xl:ml-[5%] 2xl:max-w-[85%] px-3 sm:px-4 py-4 overflow-x-hidden">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {loading ? (
            <>
              <SkeletonStatsCard />
              <SkeletonStatsCard />
              <SkeletonStatsCard />
              <SkeletonStatsCard />
            </>
          ) : (
            <>
              {/* Total Questions Card */}
              <div className="bg-white rounded-lg shadow border border-gray-100 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('questionsManagement.totalQuestions')}</p>
                    <p className="text-lg sm:text-xl font-bold text-blue-600">{stats.totalQuestions}</p>
                    <div className="text-xs text-gray-500">{t('questionsManagement.allQuestions')}</div>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <FaQuestionCircle className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  </div>
                </div>
              </div>
      
              {/* Total Sectors Card */}
              <div className="bg-white rounded-lg shadow border border-gray-100 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('questionsManagement.totalSectors')}</p>
                    <p className="text-lg sm:text-xl font-bold text-purple-600">{stats.totalSectors}</p>
                    <div className="text-xs text-gray-500">{t('questionsManagement.uniqueSectors')}</div>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <FaIndustry className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  </div>
                </div>
              </div>
      
              {/* Total Industries Card */}
              <div className="bg-white rounded-lg shadow border border-gray-100 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('questionsManagement.totalIndustries')}</p>
                    <p className="text-lg sm:text-xl font-bold text-green-600">{stats.totalIndustries}</p>
                    <div className="text-xs text-gray-500">{t('questionsManagement.uniqueIndustries')}</div>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <FaChartBar className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                  </div>
                </div>
              </div>
      
              {/* Average Questions per Sector Card */}
              <div className="bg-white rounded-lg shadow border border-gray-100 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('questionsManagement.avgPerSector')}</p>
                    <p className="text-lg sm:text-xl font-bold text-orange-600">{stats.avgQuestionsPerSector}</p>
                    <div className="text-xs text-gray-500">{t('questionsManagement.questionsPerSector')}</div>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <FaTag className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
  
        {/* Compact Search and Add Question Section - Responsive */}
        <div className="bg-white rounded-lg shadow border border-gray-100 p-3 mb-4">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-3">
            <div className="flex flex-col md:flex-row items-center gap-3 flex-1 w-full">
              <div className="relative flex-1 max-w-md">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                <input
                  type="text"
                  placeholder={t('questionsManagement.searchPlaceholder')}
                  className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                  value={searchTerm}
                  onChange={handleSearch}
                  autoComplete="off"
                />
              </div>
  
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm ${showFilters
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  <FaFilter className="w-3 h-3" />
                  <span className="hidden sm:inline">{t('common.filter')}</span>
                </button>
  
                <button
                  onClick={exportQuestions}
                  className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2 text-sm"
                >
                  <FaDownload className="w-3 h-3" />
                  <span className="hidden sm:inline">{t('reporting.export')}</span>
                </button>
              </div>
            </div>
  
            <button
              className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 text-sm w-full md:w-auto justify-center"
              onClick={() => {
                setSelectedQuestion(null);
                setNewUnitTagInput('');
                setShowModal(true);
              }}
            >
              <FaPlus className="w-3 h-3" />
              {t('questionsManagement.addQuestion')}
            </button>
          </div>
  
          {/* Advanced Filters - Responsive */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.filterBySector')}</label>
                  <select
                    value={filterSector}
                    onChange={(e) => setFilterSector(e.target.value)}
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                  >
                    <option value="all">{t('questionsManagement.allSectors')}</option>
                    {[...new Set(questions.map(q => q.sector))].map(sector => (
                      <option key={sector} value={sector}>{sector}</option>
                    ))}
                  </select>
                </div>
  
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.filterByIndustry')}</label>
                  <select
                    value={filterIndustry}
                    onChange={(e) => setFilterIndustry(e.target.value)}
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                  >
                    <option value="all">{t('questionsManagement.allIndustries')}</option>
                    {[...new Set(questions.map(q => q.industry))].map(industry => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.filterBySection91')}</label>
                  <select
                    value={filterSection91}
                    onChange={(e) => setFilterSection91(e.target.value)}
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                  >
                    <option value="all">{t('questionsManagement.filterSection91All')}</option>
                    <option value="yes">{t('questionsManagement.filterSection91Yes')}</option>
                    <option value="no">{t('questionsManagement.filterSection91No')}</option>
                  </select>
                </div>
  
                <div className="flex items-end sm:col-span-2 lg:col-span-1">
                  <button
                    type="button"
                    onClick={() => {
                      setFilterSector('all');
                      setFilterIndustry('all');
                      setFilterSection91('all');
                      setSearchTerm('');
                    }}
                    className="w-full px-2 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm"
                  >
{t('questionsManagement.clearFilters')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
  
        {/* Compact Questions Table - Mobile Card Layout */}
        <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-green-700 px-3 sm:px-4 py-3">
            <div className="flex items-center space-x-2">
              <FaQuestionCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              <div>
                <h2 className="text-white text-sm sm:text-base font-semibold">
                  {t('questionsManagement.questionsList')}
                </h2>
                <p className="text-green-100 text-xs">{t('questionsManagement.manageOrganize')}</p>
              </div>
            </div>
          </div>
  
          {/* Mobile Card Layout */}
          <div className="lg:hidden">
            {loading ? (
              <div className="p-3 space-y-2">
                <SkeletonMobileCard />
                <SkeletonMobileCard />
                <SkeletonMobileCard />
                <SkeletonMobileCard />
                <SkeletonMobileCard />
              </div>
            ) : error ? (
              <div className="flex justify-center items-center py-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FaQuestionCircle className="w-6 h-6 text-red-500" />
                  </div>
                  <p className="text-red-600 text-sm font-semibold">{error}</p>
                </div>
              </div>
            ) : filteredQuestions.length > 0 ? (
              <div className="p-3 space-y-2">
                {filteredQuestions.map((question, index) => (
                  <div key={question.id} className="bg-gray-50 rounded p-2 border border-gray-200">
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center space-x-1">
                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                          {String(index + 1).padStart(2, '0')}
                        </div>
                        {/* Number of questions badge hidden for now (same as table) */}
                      </div>
                      <div className="flex items-center space-x-1">
                        <button
                          type="button"
                          className="p-1 bg-slate-600 text-white rounded hover:bg-slate-700"
                          onClick={() =>
                            setViewQuestion({
                              ...question,
                              valueUnitFormat: normalizeValueUnits(question.valueUnitFormat),
                            })
                          }
                          disabled={loading}
                          title={t('questionsManagement.view')}
                        >
                          <FaEye className="w-2.5 h-2.5" />
                        </button>
                        <button
                          className="p-1 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                          onClick={() => {
                            setEditUnitTagInput('');
                            setSelectedQuestion({
                              ...question,
                              section9_1: Boolean(question.section9_1),
                              valueUnitFormat: normalizeValueUnits(question.valueUnitFormat),
                              reportingPeriodStart: toDateInputValue(question.reportingPeriodStart),
                              reportingPeriodEnd: toDateInputValue(question.reportingPeriodEnd),
                              answerInputKind: ['text', 'number', 'fill_in_blanks'].includes(
                                question.answerInputKind
                              )
                                ? question.answerInputKind
                                : 'text',
                              fillInBlanksTemplate: question.fillInBlanksTemplate || '',
                              answerIncludeUnitDropdown: Boolean(question.answerIncludeUnitDropdown),
                              showAnswerStatusOptions: question.showAnswerStatusOptions !== false,
                            });
                            setShowModal(true);
                          }}
                          disabled={loading}
                          title="Edit"
                        >
                          <FaEdit className="w-2.5 h-2.5" />
                        </button>
                        <button
                          className="p-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                          onClick={() => handleOpenSubQuestions(question)}
                          disabled={loading}
                          title="Subquestions"
                        >
                          <FaQuestionCircle className="w-2.5 h-2.5" />
                        </button>
                        <button
                          className="p-1 bg-red-500 text-white rounded hover:bg-red-600"
                          onClick={() => handleDelete(question.id)}
                          disabled={loading}
                          title="Delete"
                        >
                          <FaTrashAlt className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    </div>
  
                    <div className="text-xs font-semibold text-gray-900 mb-1 line-clamp-2" title={getQuestionText(question)}>
                      {getQuestionText(question)}
                    </div>
  
                    <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
                      {question.section9_1 && (
                        <span className="inline-block shrink-0 rounded bg-teal-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
                          9.1
                        </span>
                      )}
                      <div className="flex items-center space-x-1">
                        <FaIndustry className="w-2.5 h-2.5 text-blue-500" />
                        <span className="truncate" title={question.sector}>{question.sector}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <FaChartBar className="w-2.5 h-2.5 text-purple-500" />
                        <span className="truncate" title={question.industry}>{question.industry}</span>
                      </div>
                    </div>
  
                    <div className="mt-1 text-xs text-gray-500">
                      <span className="truncate" title={question.topicCode}>
                        {t('questionsManagement.topicCode')}: {question.topicCode || '—'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center">
                <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mb-3 mx-auto">
                  <FaQuestionCircle className="w-6 h-6 text-white" />
                </div>
                <p className="text-gray-600 text-sm font-semibold mb-1">No questions found</p>
                <p className="text-gray-400 text-xs">Try adjusting your search criteria or add new questions</p>
              </div>
            )}
          </div>
  
          {/* Desktop Table - UNCHANGED */}
          <div className="hidden lg:block">
            {loading ? (
              <div className="overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-2 py-2 text-center text-xs font-semibold text-gray-600 uppercase w-10">#</th>
                      <th className="px-2 py-2 text-center text-xs font-semibold text-gray-600 uppercase w-10" title="Section 9.1">9.1</th>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600 uppercase w-20">{t('questionsManagement.sector')}</th>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600 uppercase w-20">{t('questionsManagement.industry')}</th>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600 uppercase w-16">{t('questionsManagement.topicCode')}</th>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600 uppercase">{t('questionsManagement.question')}</th>
                      {/* <th className="px-2 py-2 text-center text-xs font-semibold text-gray-600 uppercase w-12">{t('questionsManagement.count')}</th> */}
                      <th className="px-2 py-2 text-center text-xs font-semibold text-gray-600 uppercase w-16">{t('questionsManagement.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <SkeletonTableRow />
                    <SkeletonTableRow />
                    <SkeletonTableRow />
                    <SkeletonTableRow />
                    <SkeletonTableRow />
                  </tbody>
                </table>
              </div>
            ) : error ? (
              <div className="flex justify-center items-center py-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FaQuestionCircle className="w-6 h-6 text-red-500" />
                  </div>
                  <p className="text-red-600 text-sm font-semibold">{error}</p>
                </div>
              </div>
            ) : (
              <div className="overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-2 py-2 text-center text-xs font-semibold text-gray-600 uppercase w-10">#</th>
                      <th className="px-2 py-2 text-center text-xs font-semibold text-gray-600 uppercase w-10" title="Section 9.1">
                        9.1
                      </th>
                      <th
                        className="px-2 py-2 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-200 w-20"
                        onClick={() => handleSort('sector')}
                      >
                        <div className="flex items-center gap-1">
                          {t('questionsManagement.sector')}
                          {sortField === 'sector' ? (
                            sortDirection === 'asc' ? <FaSortUp className="w-2.5 h-2.5" /> : <FaSortDown className="w-2.5 h-2.5" />
                          ) : (
                            <FaSort className="w-2.5 h-2.5 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-2 py-2 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-200 w-20"
                        onClick={() => handleSort('industry')}
                      >
                        <div className="flex items-center gap-1">
                          {t('questionsManagement.industry')}
                          {sortField === 'industry' ? (
                            sortDirection === 'asc' ? <FaSortUp className="w-2.5 h-2.5" /> : <FaSortDown className="w-2.5 h-2.5" />
                          ) : (
                            <FaSort className="w-2.5 h-2.5 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-2 py-2 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-200 w-16"
                        onClick={() => handleSort('topicCode')}
                      >
                        <div className="flex items-center gap-1">
                          {t('questionsManagement.topicCode')}
                          {sortField === 'topicCode' ? (
                            sortDirection === 'asc' ? <FaSortUp className="w-2.5 h-2.5" /> : <FaSortDown className="w-2.5 h-2.5" />
                          ) : (
                            <FaSort className="w-2.5 h-2.5 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-2 py-2 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-200"
                        onClick={() => handleSort('engagementQuestion')}
                      >
                        <div className="flex items-center gap-1">
                          {t('questionsManagement.question')}
                          {sortField === 'engagementQuestion' ? (
                            sortDirection === 'asc' ? <FaSortUp className="w-2.5 h-2.5" /> : <FaSortDown className="w-2.5 h-2.5" />
                          ) : (
                            <FaSort className="w-2.5 h-2.5 text-gray-400" />
                          )}
                        </div>
                      </th>
                      {/* <th className="px-2 py-2 text-center text-xs font-semibold text-gray-600 uppercase w-12">{t('questionsManagement.count')}</th> */}
                      <th className="px-2 py-2 text-center text-xs font-semibold text-gray-600 uppercase w-16">{t('questionsManagement.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredQuestions.length > 0 ? (
                      filteredQuestions.map((question, index) => (
                        <tr
                          key={question.id}
                          className="hover:bg-green-50"
                        >
                          <td className="px-2 py-2 whitespace-nowrap text-center text-xs font-medium text-gray-900">
                            <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold mx-auto">
                              {String(index + 1).padStart(2, '0')}
                            </div>
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap text-center text-xs">
                            {question.section9_1 ? (
                              <span className="inline-block rounded bg-teal-600 px-1 py-0.5 text-[10px] font-bold text-white">Y</span>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center mr-1 flex-shrink-0">
                                <FaIndustry className="w-2 h-2 text-white" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-xs font-semibold text-gray-900 truncate" title={question.sector}>
                                  {question.sector}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center mr-1 flex-shrink-0">
                                <FaChartBar className="w-2 h-2 text-white" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-xs font-semibold text-gray-900 truncate" title={question.industry}>
                                  {question.industry}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-4 h-4 bg-orange-500 rounded-full flex items-center justify-center mr-1 flex-shrink-0">
                                <FaCode className="w-2 h-2 text-white" />
                              </div>
                              <div className="text-xs text-gray-700 truncate" title={question.topicCode}>
                                {question.topicCode || '—'}
                              </div>
                            </div>
                          </td>
                          <td className="px-2 py-2">
                            <div className="text-xs text-gray-900 line-clamp-2 min-h-[2rem]" title={getQuestionText(question)}>
                              {getQuestionText(question)}
                            </div>
                          </td>
                          {/* <td className="px-2 py-2 whitespace-nowrap text-center">
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-bold bg-green-500 text-white">
                              {question.numberOfQuestions}
                            </span>
                          </td> */}
                          <td className="px-2 py-2 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center space-x-1">
                              <button
                                type="button"
                                className="p-1 bg-slate-600 text-white rounded hover:bg-slate-700"
                                onClick={() =>
                                  setViewQuestion({
                                    ...question,
                                    valueUnitFormat: normalizeValueUnits(question.valueUnitFormat),
                                  })
                                }
                                disabled={loading}
                                title={t('questionsManagement.view')}
                              >
                                <FaEye className="w-2.5 h-2.5" />
                              </button>
                              <button
                                className="p-1 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                                onClick={() => {
                                  setEditUnitTagInput('');
                                  setSelectedQuestion({
                                    ...question,
                                    section9_1: Boolean(question.section9_1),
                                    valueUnitFormat: normalizeValueUnits(question.valueUnitFormat),
                                    reportingPeriodStart: toDateInputValue(question.reportingPeriodStart),
                                    reportingPeriodEnd: toDateInputValue(question.reportingPeriodEnd),
                                    answerInputKind: ['text', 'number', 'fill_in_blanks'].includes(
                                      question.answerInputKind
                                    )
                                      ? question.answerInputKind
                                      : 'text',
                                    fillInBlanksTemplate: question.fillInBlanksTemplate || '',
                                    answerIncludeUnitDropdown: Boolean(question.answerIncludeUnitDropdown),
                                    showAnswerStatusOptions: question.showAnswerStatusOptions !== false,
                                  });
                                  setShowModal(true);
                                }}
                                disabled={loading}
                                title="Edit"
                              >
                                <FaEdit className="w-2.5 h-2.5" />
                              </button>
                              <button
                                className="p-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                onClick={() => handleOpenSubQuestions(question)}
                                disabled={loading}
                                title="Subquestions"
                              >
                                <FaQuestionCircle className="w-2.5 h-2.5" />
                              </button>
                              <button
                                className="p-1 bg-red-500 text-white rounded hover:bg-red-600"
                                onClick={() => handleDelete(question.id)}
                                disabled={loading}
                                title="Delete"
                              >
                                <FaTrashAlt className="w-2.5 h-2.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center">
                          <div className="flex flex-col items-center">
                            <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mb-3">
                              <FaQuestionCircle className="w-6 h-6 text-white" />
                            </div>
                            <p className="text-gray-600 text-sm font-semibold mb-1">No questions found</p>
                            <p className="text-gray-400 text-xs">Try adjusting your search criteria or add new questions</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
  
      {/* Compact Update Modal - Responsive */}
      {showModal && selectedQuestion && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-2 pb-16 pt-6 sm:items-center sm:px-4 sm:py-8 sm:pb-8">
          <div className="my-auto flex w-full min-w-0 max-w-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg sm:max-w-lg md:max-w-2xl max-h-[min(92dvh,56rem)]">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-3 sm:px-4 py-3 shrink-0">
              <h3 className="text-white text-sm sm:text-base font-semibold flex items-center">
                <FaEdit className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                {t('questionsManagement.editQuestion')} #{selectedQuestion.id}
              </h3>
              <p className="text-green-100 text-xs">{t('questionsManagement.updateQuestionDetails')}</p>
            </div>
  
            <div className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-4">
              <form onSubmit={handleUpdate} className="space-y-2">
                <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50/80 px-2 py-2">
                  <input
                    type="checkbox"
                    id="edit-section91"
                    checked={!!selectedQuestion.section9_1}
                    onChange={(e) =>
                      setSelectedQuestion({ ...selectedQuestion, section9_1: e.target.checked })
                    }
                    className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <label htmlFor="edit-section91" className="text-xs font-semibold text-gray-700 cursor-pointer">
                    {t('questionsManagement.section91Feature')}
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {[
                    { field: 'sectorCode', labelKey: 'questionsManagement.sectorCode', type: 'text' },
                    { field: 'topicCode', labelKey: 'questionsManagement.topicCode', type: 'text' },
                    { field: 'industryCode', labelKey: 'questionsManagement.industryCode', type: 'text' },
                    /* { field: 'numberOfQuestions', labelKey: 'questionsManagement.numberOfQuestions', type: 'number' }, */
                    /* questionIdentifier removed — synced from topic code on save */
                  ].map(({ field, labelKey, type }) => (
                    <div key={field}>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">{t(labelKey)}</label>
                      <input
                        type={type}
                        value={selectedQuestion[field] || ''}
                        onChange={(e) =>
                          setSelectedQuestion({ ...selectedQuestion, [field]: e.target.value })
                        }
                        className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                      />
                    </div>
                  ))}
                  
                  {/* Industry Dropdown */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.industry')}</label>
                    <select
                      value={selectedQuestion.industry ? String(selectedQuestion.industry).trim() : ''}
                      onChange={(e) =>
                        setSelectedQuestion({ ...selectedQuestion, industry: e.target.value })
                      }
                      className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                    >
                      <option value="">{t('questionsManagement.selectIndustry') || 'Select Industry'}</option>
                      {industries.map((industry) => (
                        <option key={industry.id} value={industry.sectorName}>
                          {industry.sectorName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Sector Dropdown */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.sector')}</label>
                    <select
                      value={selectedQuestion.sector ? String(selectedQuestion.sector).trim() : ''}
                      onChange={(e) =>
                        setSelectedQuestion({ ...selectedQuestion, sector: e.target.value })
                      }
                      className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                    >
                      <option value="">{t('questionsManagement.selectSector') || 'Select Sector'}</option>
                      {sectors.map((sector) => (
                        <option key={sector.id} value={sector.industryName}>
                          {sector.industryName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.disclosureTopic')}</label>
                  <select
                    value={selectedQuestion.disclosureTopicFieldId || ''}
                    onChange={(e) =>
                      setSelectedQuestion({ ...selectedQuestion, disclosureTopicFieldId: e.target.value })
                    }
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                  >
                    <option value="">{t('questionsManagement.selectTopic')}</option>
                    {disclosureTopics.map((topic) => (
                      <option key={topic.id} value={topic.id}>
                        {topic.standards} - {topic.disclosureTopic} - {topic.disclosureField}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.engagementQuestionEnglish')} *</label>
                  <textarea
                    value={selectedQuestion.engagementQuestion || ''}
                    onChange={(e) =>
                      setSelectedQuestion({ ...selectedQuestion, engagementQuestion: e.target.value })
                    }
                    rows={2}
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm resize-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.engagementQuestionSpanish')} <span className="text-gray-500 text-xs">({t('questionsManagement.optional')})</span></label>
                  <textarea
                    value={selectedQuestion.engagementQuestionEs || ''}
                    onChange={(e) =>
                      setSelectedQuestion({ ...selectedQuestion, engagementQuestionEs: e.target.value })
                    }
                    rows={2}
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm resize-none"
                    placeholder="Pregunta de participación (Español)"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    {t('questionsManagement.cmfRequirementEnglish')}{' '}
                    <span className="text-xs font-normal text-gray-500">({t('questionsManagement.optional')})</span>
                  </label>
                  <textarea
                    value={selectedQuestion.cmfRequirement || ''}
                    onChange={(e) =>
                      setSelectedQuestion({ ...selectedQuestion, cmfRequirement: e.target.value })
                    }
                    rows={2}
                    className="w-full resize-none rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    {t('questionsManagement.cmfRequirementSpanish')}{' '}
                    <span className="text-xs font-normal text-gray-500">({t('questionsManagement.optional')})</span>
                  </label>
                  <textarea
                    value={selectedQuestion.cmfRequirementEs || ''}
                    onChange={(e) =>
                      setSelectedQuestion({ ...selectedQuestion, cmfRequirementEs: e.target.value })
                    }
                    rows={2}
                    className="w-full resize-none rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-green-500"
                    placeholder={t('questionsManagement.cmfRequirementSpanishPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.ncg30461Article')}</label>
                  <textarea
                    value={selectedQuestion.ncg30461Article || ''}
                    onChange={(e) =>
                      setSelectedQuestion({ ...selectedQuestion, ncg30461Article: e.target.value })
                    }
                    rows={2}
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm resize-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.ncg30519Article')}</label>
                  <textarea
                    value={selectedQuestion.ncg30519Article || ''}
                    onChange={(e) =>
                      setSelectedQuestion({ ...selectedQuestion, ncg30519Article: e.target.value })
                    }
                    rows={2}
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm resize-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.valueUnitFormat')}</label>
                  <div className="mb-1 flex flex-wrap gap-1">
                    {(selectedQuestion.valueUnitFormat || []).map((tag, idx) => (
                      <span
                        key={`${tag}-${idx}`}
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900"
                      >
                        {tag}
                        <button
                          type="button"
                          className="rounded p-0.5 hover:bg-emerald-200"
                          onClick={() =>
                            setSelectedQuestion({
                              ...selectedQuestion,
                              valueUnitFormat: (selectedQuestion.valueUnitFormat || []).filter((_, i) => i !== idx),
                            })
                          }
                          aria-label={t('questionsManagement.removeUnitTag')}
                        >
                          <FaTimes className="h-2.5 w-2.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={editUnitTagInput}
                      onChange={(e) => setEditUnitTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const v = editUnitTagInput.trim();
                          if (!v) return;
                          const list = [...(selectedQuestion.valueUnitFormat || [])];
                          if (!list.includes(v)) list.push(v);
                          setSelectedQuestion({ ...selectedQuestion, valueUnitFormat: list });
                          setEditUnitTagInput('');
                        }
                      }}
                      placeholder={t('questionsManagement.valueUnitFormatPlaceholder')}
                      className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-green-500"
                    />
                    <button
                      type="button"
                      className="shrink-0 rounded-lg bg-emerald-600 px-2 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                      onClick={() => {
                        const v = editUnitTagInput.trim();
                        if (!v) return;
                        const list = [...(selectedQuestion.valueUnitFormat || [])];
                        if (!list.includes(v)) list.push(v);
                        setSelectedQuestion({ ...selectedQuestion, valueUnitFormat: list });
                        setEditUnitTagInput('');
                      }}
                    >
                      {t('questionsManagement.addTag')}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.sasbMetricName')}</label>
                  <input
                    type="text"
                    value={selectedQuestion.sasbMetricName || ''}
                    onChange={(e) =>
                      setSelectedQuestion({ ...selectedQuestion, sasbMetricName: e.target.value })
                    }
                    className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-green-500"
                  />
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      {t('questionsManagement.reportingPeriodStart')}
                    </label>
                    <input
                      type="date"
                      value={selectedQuestion.reportingPeriodStart || ''}
                      onChange={(e) =>
                        setSelectedQuestion({
                          ...selectedQuestion,
                          reportingPeriodStart: e.target.value,
                        })
                      }
                      className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      {t('questionsManagement.reportingPeriodEnd')}
                    </label>
                    <input
                      type="date"
                      value={selectedQuestion.reportingPeriodEnd || ''}
                      onChange={(e) =>
                        setSelectedQuestion({
                          ...selectedQuestion,
                          reportingPeriodEnd: e.target.value,
                        })
                      }
                      className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-green-500"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-gray-500">{t('questionsManagement.reportingPeriodHint')}</p>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    {t('questionsManagement.answerInputKind')}
                  </label>
                  <select
                    value={
                      ['text', 'number', 'fill_in_blanks'].includes(selectedQuestion.answerInputKind)
                        ? selectedQuestion.answerInputKind
                        : 'text'
                    }
                    onChange={(e) =>
                      setSelectedQuestion({ ...selectedQuestion, answerInputKind: e.target.value })
                    }
                    className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-green-500"
                  >
                    <option value="text">{t('questionsManagement.answerInputKindText')}</option>
                    <option value="number">{t('questionsManagement.answerInputKindNumber')}</option>
                    <option value="fill_in_blanks">
                      {t('questionsManagement.answerInputKindFillBlanks')}
                    </option>
                  </select>
                  <p className="mt-0.5 text-[11px] text-gray-500">{t('questionsManagement.answerInputKindHint')}</p>
                </div>

                <div className="flex items-center gap-2 border-t border-gray-100 pt-2">
                  <input
                    type="checkbox"
                    id="edit-show-answer-status"
                    className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    checked={selectedQuestion.showAnswerStatusOptions !== false}
                    onChange={(e) =>
                      setSelectedQuestion({
                        ...selectedQuestion,
                        showAnswerStatusOptions: e.target.checked,
                      })
                    }
                  />
                  <label
                    htmlFor="edit-show-answer-status"
                    className="cursor-pointer text-xs font-semibold text-gray-700"
                  >
                    {t('questionsManagement.showAnswerStatusOptions')}
                  </label>
                </div>
                <p className="text-[11px] text-gray-500">{t('questionsManagement.showAnswerStatusOptionsHint')}</p>

                {selectedQuestion.answerInputKind === 'fill_in_blanks' && (
                  <>
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-gray-700">
                        {t('questionsManagement.fillInBlanksTemplate')}
                      </label>
                      <textarea
                        rows={3}
                        value={selectedQuestion.fillInBlanksTemplate || ''}
                        onChange={(e) =>
                          setSelectedQuestion({
                            ...selectedQuestion,
                            fillInBlanksTemplate: e.target.value,
                          })
                        }
                        className="w-full resize-y rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-green-500"
                        placeholder={t('questionsManagement.fillInBlanksTemplatePlaceholder')}
                      />
                      <p className="mt-0.5 text-[11px] text-gray-500">
                        {t('questionsManagement.fillInBlanksTemplateHint')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 border-t border-gray-100 pt-2">
                      <input
                        type="checkbox"
                        id="edit-answer-include-unit"
                        className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        checked={Boolean(selectedQuestion.answerIncludeUnitDropdown)}
                        onChange={(e) =>
                          setSelectedQuestion({
                            ...selectedQuestion,
                            answerIncludeUnitDropdown: e.target.checked,
                          })
                        }
                      />
                      <label
                        htmlFor="edit-answer-include-unit"
                        className="cursor-pointer text-xs font-semibold text-gray-700"
                      >
                        {t('questionsManagement.answerIncludeUnitDropdown')}
                      </label>
                    </div>
                    <p className="text-[11px] text-gray-500">
                      {t('questionsManagement.answerIncludeUnitDropdownHint')}
                    </p>
                  </>
                )}
              </form>
            </div>
  
            <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50 px-3 py-2 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-2">
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setSelectedQuestion(null);
                  setEditUnitTagInput('');
                }}
                className="w-full rounded px-3 py-2 text-sm text-white bg-gray-500 hover:bg-gray-600 sm:w-auto sm:py-1.5"
              >
                {t('questionsManagement.cancel')}
              </button>
              <button
                type="button"
                onClick={handleUpdate}
                className="w-full rounded px-3 py-2 text-sm text-white bg-green-500 hover:bg-green-600 sm:w-auto sm:py-1.5"
              >
                {t('questionsManagement.updateQuestion')}
              </button>
            </div>
          </div>
        </div>
      )}
  
      {/* Compact Add Question Modal - Responsive */}
      {showModal && !selectedQuestion && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-2 pb-16 pt-6 sm:items-center sm:px-4 sm:py-8 sm:pb-8">
          <div className="my-auto flex w-full min-w-0 max-w-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg sm:max-w-lg md:max-w-2xl max-h-[min(92dvh,56rem)]">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-3 sm:px-4 py-3 shrink-0">
              <h3 className="text-white text-sm sm:text-base font-semibold flex items-center">
                <FaPlus className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                {t('questionsManagement.addNewQuestion')}
              </h3>
              <p className="text-green-100 text-xs">{t('questionsManagement.createNewESGQuestion')}</p>
            </div>
  
            <div className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-4">
              <form onSubmit={handleAddQuestion} className="space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {[
                    { field: 'sectorCode', labelKey: 'questionsManagement.sectorCode', type: 'text' },
                    { field: 'topicCode', labelKey: 'questionsManagement.topicCode', type: 'text' },
                    { field: 'industryCode', labelKey: 'questionsManagement.industryCode', type: 'text' },
                    /* numberOfQuestions + questionIdentifier omitted — count fixed on save; identifier = topic code */
                  ].map(({ field, labelKey, type }) => (
                    <div key={field}>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">{t(labelKey)}</label>
                      <input
                        type={type}
                        value={newQuestion[field] || ''}
                        onChange={(e) =>
                          setNewQuestion({ ...newQuestion, [field]: e.target.value })
                        }
                        className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                      />
                    </div>
                  ))}
                  
                  {/* Industry Dropdown */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.industry')}</label>
                    <select
                      value={newQuestion.industry || ''}
                      onChange={(e) =>
                        setNewQuestion({ ...newQuestion, industry: e.target.value })
                      }
                      className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                    >
                      <option value="">{t('questionsManagement.selectIndustry') || 'Select Industry'}</option>
                      {industries.map((industry) => (
                        <option key={industry.id} value={industry.sectorName}>
                          {industry.sectorName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Sector Dropdown */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.sector')}</label>
                    <select
                      value={newQuestion.sector || ''}
                      onChange={(e) =>
                        setNewQuestion({ ...newQuestion, sector: e.target.value })
                      }
                      className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                    >
                      <option value="">{t('questionsManagement.selectSector') || 'Select Sector'}</option>
                      {sectors.map((sector) => (
                        <option key={sector.id} value={sector.industryName}>
                          {sector.industryName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
  
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.disclosureTopic')}</label>
                  <select
                    value={newQuestion.disclosureTopicFieldId || ''}
                    onChange={(e) =>
                      setNewQuestion({ ...newQuestion, disclosureTopicFieldId: e.target.value })
                    }
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                  >
                    <option value="">{t('questionsManagement.selectTopic')}</option>
                    {disclosureTopics.map((topic) => (
                      <option key={topic.id} value={topic.id}>
                        {topic.standards} - {topic.disclosureTopic} - {topic.disclosureField}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.engagementQuestionEnglish')} *</label>
                  <textarea
                    value={newQuestion.engagementQuestion || ''}
                    onChange={(e) =>
                      setNewQuestion({ ...newQuestion, engagementQuestion: e.target.value })
                    }
                    rows={2}
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm resize-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.engagementQuestionSpanish')} <span className="text-gray-500 text-xs">({t('questionsManagement.optional')})</span></label>
                  <textarea
                    value={newQuestion.engagementQuestionEs || ''}
                    onChange={(e) =>
                      setNewQuestion({ ...newQuestion, engagementQuestionEs: e.target.value })
                    }
                    rows={2}
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm resize-none"
                    placeholder="Pregunta de participación (Español)"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    {t('questionsManagement.cmfRequirementEnglish')}{' '}
                    <span className="text-xs font-normal text-gray-500">({t('questionsManagement.optional')})</span>
                  </label>
                  <textarea
                    value={newQuestion.cmfRequirement || ''}
                    onChange={(e) =>
                      setNewQuestion({ ...newQuestion, cmfRequirement: e.target.value })
                    }
                    rows={2}
                    className="w-full resize-none rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    {t('questionsManagement.cmfRequirementSpanish')}{' '}
                    <span className="text-xs font-normal text-gray-500">({t('questionsManagement.optional')})</span>
                  </label>
                  <textarea
                    value={newQuestion.cmfRequirementEs || ''}
                    onChange={(e) =>
                      setNewQuestion({ ...newQuestion, cmfRequirementEs: e.target.value })
                    }
                    rows={2}
                    className="w-full resize-none rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-green-500"
                    placeholder={t('questionsManagement.cmfRequirementSpanishPlaceholder')}
                  />
                </div>

                <div className="flex items-center gap-2 border-t border-gray-100 pt-2 mt-1">
                  <input
                    type="checkbox"
                    id="new-section91"
                    checked={!!newQuestion.section9_1}
                    onChange={(e) =>
                      setNewQuestion({ ...newQuestion, section9_1: e.target.checked })
                    }
                    className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <label htmlFor="new-section91" className="text-xs font-semibold text-gray-700 cursor-pointer">
                    {t('questionsManagement.section91Feature')}
                  </label>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.ncg30461Article')}</label>
                  <textarea
                    value={newQuestion.ncg30461Article || ''}
                    onChange={(e) =>
                      setNewQuestion({ ...newQuestion, ncg30461Article: e.target.value })
                    }
                    rows={2}
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm resize-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.ncg30519Article')}</label>
                  <textarea
                    value={newQuestion.ncg30519Article || ''}
                    onChange={(e) =>
                      setNewQuestion({ ...newQuestion, ncg30519Article: e.target.value })
                    }
                    rows={2}
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm resize-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.valueUnitFormat')}</label>
                  <div className="mb-1 flex flex-wrap gap-1">
                    {(newQuestion.valueUnitFormat || []).map((tag, idx) => (
                      <span
                        key={`${tag}-${idx}`}
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900"
                      >
                        {tag}
                        <button
                          type="button"
                          className="rounded p-0.5 hover:bg-emerald-200"
                          onClick={() =>
                            setNewQuestion({
                              ...newQuestion,
                              valueUnitFormat: (newQuestion.valueUnitFormat || []).filter((_, i) => i !== idx),
                            })
                          }
                          aria-label={t('questionsManagement.removeUnitTag')}
                        >
                          <FaTimes className="h-2.5 w-2.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={newUnitTagInput}
                      onChange={(e) => setNewUnitTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const v = newUnitTagInput.trim();
                          if (!v) return;
                          const list = [...(newQuestion.valueUnitFormat || [])];
                          if (!list.includes(v)) list.push(v);
                          setNewQuestion({ ...newQuestion, valueUnitFormat: list });
                          setNewUnitTagInput('');
                        }
                      }}
                      placeholder={t('questionsManagement.valueUnitFormatPlaceholder')}
                      className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-green-500"
                    />
                    <button
                      type="button"
                      className="shrink-0 rounded-lg bg-emerald-600 px-2 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                      onClick={() => {
                        const v = newUnitTagInput.trim();
                        if (!v) return;
                        const list = [...(newQuestion.valueUnitFormat || [])];
                        if (!list.includes(v)) list.push(v);
                        setNewQuestion({ ...newQuestion, valueUnitFormat: list });
                        setNewUnitTagInput('');
                      }}
                    >
                      {t('questionsManagement.addTag')}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questionsManagement.sasbMetricName')}</label>
                  <input
                    type="text"
                    value={newQuestion.sasbMetricName || ''}
                    onChange={(e) =>
                      setNewQuestion({ ...newQuestion, sasbMetricName: e.target.value })
                    }
                    className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-green-500"
                  />
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      {t('questionsManagement.reportingPeriodStart')}
                    </label>
                    <input
                      type="date"
                      value={newQuestion.reportingPeriodStart || ''}
                      onChange={(e) =>
                        setNewQuestion({ ...newQuestion, reportingPeriodStart: e.target.value })
                      }
                      className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      {t('questionsManagement.reportingPeriodEnd')}
                    </label>
                    <input
                      type="date"
                      value={newQuestion.reportingPeriodEnd || ''}
                      onChange={(e) =>
                        setNewQuestion({ ...newQuestion, reportingPeriodEnd: e.target.value })
                      }
                      className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-green-500"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-gray-500">{t('questionsManagement.reportingPeriodHint')}</p>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    {t('questionsManagement.answerInputKind')}
                  </label>
                  <select
                    value={
                      ['text', 'number', 'fill_in_blanks'].includes(newQuestion.answerInputKind)
                        ? newQuestion.answerInputKind
                        : 'text'
                    }
                    onChange={(e) =>
                      setNewQuestion({ ...newQuestion, answerInputKind: e.target.value })
                    }
                    className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-green-500"
                  >
                    <option value="text">{t('questionsManagement.answerInputKindText')}</option>
                    <option value="number">{t('questionsManagement.answerInputKindNumber')}</option>
                    <option value="fill_in_blanks">
                      {t('questionsManagement.answerInputKindFillBlanks')}
                    </option>
                  </select>
                  <p className="mt-0.5 text-[11px] text-gray-500">{t('questionsManagement.answerInputKindHint')}</p>
                </div>

                <div className="flex items-center gap-2 border-t border-gray-100 pt-2">
                  <input
                    type="checkbox"
                    id="new-show-answer-status"
                    className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    checked={newQuestion.showAnswerStatusOptions !== false}
                    onChange={(e) =>
                      setNewQuestion({
                        ...newQuestion,
                        showAnswerStatusOptions: e.target.checked,
                      })
                    }
                  />
                  <label
                    htmlFor="new-show-answer-status"
                    className="cursor-pointer text-xs font-semibold text-gray-700"
                  >
                    {t('questionsManagement.showAnswerStatusOptions')}
                  </label>
                </div>
                <p className="text-[11px] text-gray-500">{t('questionsManagement.showAnswerStatusOptionsHint')}</p>

                {newQuestion.answerInputKind === 'fill_in_blanks' && (
                  <>
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-gray-700">
                        {t('questionsManagement.fillInBlanksTemplate')}
                      </label>
                      <textarea
                        rows={3}
                        value={newQuestion.fillInBlanksTemplate || ''}
                        onChange={(e) =>
                          setNewQuestion({ ...newQuestion, fillInBlanksTemplate: e.target.value })
                        }
                        className="w-full resize-y rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-green-500"
                        placeholder={t('questionsManagement.fillInBlanksTemplatePlaceholder')}
                      />
                      <p className="mt-0.5 text-[11px] text-gray-500">
                        {t('questionsManagement.fillInBlanksTemplateHint')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 border-t border-gray-100 pt-2">
                      <input
                        type="checkbox"
                        id="new-answer-include-unit"
                        className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        checked={Boolean(newQuestion.answerIncludeUnitDropdown)}
                        onChange={(e) =>
                          setNewQuestion({
                            ...newQuestion,
                            answerIncludeUnitDropdown: e.target.checked,
                          })
                        }
                      />
                      <label
                        htmlFor="new-answer-include-unit"
                        className="cursor-pointer text-xs font-semibold text-gray-700"
                      >
                        {t('questionsManagement.answerIncludeUnitDropdown')}
                      </label>
                    </div>
                    <p className="text-[11px] text-gray-500">
                      {t('questionsManagement.answerIncludeUnitDropdownHint')}
                    </p>
                  </>
                )}
              </form>
            </div>
  
            <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50 px-3 py-2 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-2">
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setNewUnitTagInput('');
                  setNewQuestion({
                    sectorCode: '',
                    topicCode: '',
                    industry: '',
                    industryCode: '',
                    sector: '',
                    numberOfQuestions: '',
                    questionIdentifier: '',
                    engagementQuestion: '',
                    engagementQuestionEs: '',
                    cmfRequirement: '',
                    cmfRequirementEs: '',
                    disclosureTopic: '',
                    disclosureTopicFieldId: '',
                    section9_1: false,
                    ncg30461Article: '',
                    ncg30519Article: '',
                    valueUnitFormat: [],
                    sasbMetricName: '',
                    reportingPeriodStart: '',
                    reportingPeriodEnd: '',
                    answerInputKind: 'text',
                    fillInBlanksTemplate: '',
                    answerIncludeUnitDropdown: false,
                    showAnswerStatusOptions: true
                  });
                }}
                className="w-full rounded px-3 py-2 text-sm text-white bg-gray-500 hover:bg-gray-600 sm:w-auto sm:py-1.5"
              >
                {t('questionsManagement.cancel')}
              </button>
              <button
                type="button"
                onClick={handleAddQuestion}
                className="w-full rounded px-3 py-2 text-sm text-white bg-green-500 hover:bg-green-600 sm:w-auto sm:py-1.5"
              >
                {t('questionsManagement.addQuestion')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View question (read-only) */}
      {viewQuestion && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-2 pb-16 pt-6 sm:items-center sm:px-4 sm:py-8 sm:pb-8">
          <div className="my-auto flex w-full min-w-0 max-w-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg sm:max-w-lg md:max-w-2xl max-h-[min(92dvh,56rem)]">
            <div className="shrink-0 bg-gradient-to-r from-green-600 to-green-700 px-3 py-3 sm:px-4">
              <h3 className="flex items-center text-sm font-semibold text-white sm:text-base">
                <FaEye className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                {t('questionsManagement.viewQuestionTitle', { id: viewQuestion.id })}
              </h3>
              <p className="text-xs text-green-100">{t('questionsManagement.viewQuestionSubtitle')}</p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-3 text-sm sm:p-4">
              <div className="space-y-0 divide-y divide-gray-100 rounded-lg border border-gray-100 bg-gray-50/50">
                {[
                  [t('questionsManagement.sector'), viewQuestion.sector || '—'],
                  [t('questionsManagement.industry'), viewQuestion.industry || '—'],
                  [t('questionsManagement.sectorCode'), viewQuestion.sectorCode || '—'],
                  [t('questionsManagement.topicCode'), viewQuestion.topicCode || '—'],
                  [t('questionsManagement.industryCode'), viewQuestion.industryCode || '—'],
                  /* [t('questionsManagement.numberOfQuestions'), viewQuestion.numberOfQuestions ?? '—'], */
                  [t('questionsManagement.disclosureTopic'), viewQuestion.disclosureTopic || '—'],
                  [t('questionsManagement.section91Feature'), viewQuestion.section9_1 ? t('common.yes') : t('common.no')],
                  [
                    t('questionsManagement.answerInputKind'),
                    viewQuestion.answerInputKind === 'number'
                      ? t('questionsManagement.answerInputKindNumber')
                      : viewQuestion.answerInputKind === 'fill_in_blanks'
                        ? t('questionsManagement.answerInputKindFillBlanks')
                        : t('questionsManagement.answerInputKindText'),
                  ],
                  [
                    t('questionsManagement.showAnswerStatusOptions'),
                    viewQuestion.showAnswerStatusOptions !== false ? t('common.yes') : t('common.no'),
                  ],
                  ...(viewQuestion.answerInputKind === 'fill_in_blanks'
                    ? [
                        [
                          t('questionsManagement.fillInBlanksTemplate'),
                          viewQuestion.fillInBlanksTemplate || '—',
                        ],
                        [
                          t('questionsManagement.answerIncludeUnitDropdown'),
                          viewQuestion.answerIncludeUnitDropdown ? t('common.yes') : t('common.no'),
                        ],
                      ]
                    : []),
                  [t('questionsManagement.engagementQuestionEnglish'), viewQuestion.engagementQuestion || '—'],
                  [t('questionsManagement.engagementQuestionSpanish'), viewQuestion.engagementQuestionEs || '—'],
                  [t('questionsManagement.cmfRequirementEnglish'), viewQuestion.cmfRequirement || '—'],
                  [t('questionsManagement.cmfRequirementSpanish'), viewQuestion.cmfRequirementEs || '—'],
                  [t('questionsManagement.ncg30461Article'), viewQuestion.ncg30461Article || '—'],
                  [t('questionsManagement.ncg30519Article'), viewQuestion.ncg30519Article || '—'],
                  [t('questionsManagement.sasbMetricName'), viewQuestion.sasbMetricName || '—'],
                  [t('questionsManagement.reportingPeriodStart'), formatDisplayDate(viewQuestion.reportingPeriodStart)],
                  [t('questionsManagement.reportingPeriodEnd'), formatDisplayDate(viewQuestion.reportingPeriodEnd)],
                ].map(([label, val]) => (
                  <div key={label} className="px-2 py-2 sm:px-3">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
                    <div className="mt-0.5 whitespace-pre-wrap break-words text-gray-900">{val}</div>
                  </div>
                ))}
                <div className="px-2 py-2 sm:px-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                    {t('questionsManagement.valueUnitFormat')}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(viewQuestion.valueUnitFormat || []).length ? (
                      viewQuestion.valueUnitFormat.map((tag, idx) => (
                        <span
                          key={`${tag}-${idx}`}
                          className="inline-block rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900"
                        >
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex shrink-0 justify-stretch border-t border-gray-100 bg-gray-50 px-3 py-2 sm:justify-end">
              <button
                type="button"
                onClick={() => setViewQuestion(null)}
                className="w-full rounded px-3 py-2 text-sm text-white bg-green-500 hover:bg-green-600 sm:w-auto sm:py-1.5"
              >
                {t('questionsManagement.closeView')}
              </button>
            </div>
          </div>
        </div>
      )}
  
      {/* Compact SubQuestions Management Modal - Responsive */}
      {showSubQuestionsModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-2 pb-16 pt-6 sm:items-center sm:px-4 sm:py-8 sm:pb-8">
          <div className="my-auto flex w-full min-w-0 max-w-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg sm:max-w-md max-h-[min(92dvh,40rem)]">
            <div className="shrink-0 bg-gradient-to-r from-blue-600 to-blue-700 px-3 sm:px-4 py-3">
              <h3 className="text-white text-sm sm:text-base font-semibold flex items-center">
                <FaQuestionCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                Manage Subquestions
              </h3>
              <p className="text-blue-100 text-xs">
                Question: {selectedQuestionForSubQuestions?.engagementQuestion?.substring(0, 50)}...
              </p>
            </div>
  
            <div className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-4">
              {/* Add New SubQuestion */}
              <div className="mb-3 rounded bg-gray-50 p-2">
                <h4 className="text-sm font-semibold text-gray-700 mb-1">Add New SubQuestion</h4>
                <div className="flex gap-1">
                  <input
                    type="text"
                    placeholder="Enter subquestion text..."
                    className="flex-1 px-2 py-1.5 border border-gray-200 rounded focus:border-blue-500 text-sm"
                    value={newSubQuestion.subQuestionText}
                    onChange={(e) => setNewSubQuestion({ subQuestionText: e.target.value })}
                  />
                  <button
                    onClick={handleAddSubQuestion}
                    className="px-2 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                  >
                    Add
                  </button>
                </div>
              </div>
  
              {/* Existing SubQuestions */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-1">Existing SubQuestions ({subQuestions.length})</h4>
                {subQuestions.length > 0 ? (
                  <div className="space-y-1">
                    {subQuestions.map((subQuestion, index) => (
                      <div key={subQuestion.id} className="p-2 bg-white border border-gray-200 rounded flex items-center justify-between">
                        <div className="flex-1">
                          <span className="text-xs font-medium text-gray-500 mr-1">{index + 1}.</span>
                          <span className="text-gray-800 text-xs">{subQuestion.subQuestionText}</span>
                        </div>
                        <button
                          onClick={() => handleDeleteSubQuestion(subQuestion.id)}
                          className="p-1 bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          <FaTrashAlt className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <FaQuestionCircle className="w-8 h-8 mx-auto mb-1 text-gray-300" />
                    <p className="text-xs">No subquestions added yet</p>
                  </div>
                )}
              </div>
            </div>
  
            <div className="flex shrink-0 justify-stretch border-t border-gray-100 bg-gray-50 px-3 py-2 sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowSubQuestionsModal(false);
                  setSelectedQuestionForSubQuestions(null);
                  setSubQuestions([]);
                  setNewSubQuestion({ subQuestionText: '' });
                }}
                className="w-full rounded px-3 py-2 text-sm text-white bg-gray-500 hover:bg-gray-600 sm:w-auto sm:py-1.5"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListQuestionsForManagement;
